import { PaymentService } from '@qdrant/qdrant-cloud-public-api/payment/v1';
import {
  listPaymentMethods,
  getPaymentMethod,
  getStripeCheckoutSession,
  updatePaymentMethod,
  createStripeCheckoutSession,
  deletePaymentMethod,
  recordCloudMarketplaceEntitlement,
  getPaymentMethodAvailability,
} from '@qdrant/qdrant-cloud-public-api/payment/v1/connect-query';
import { listCloudProviders } from '@qdrant/qdrant-cloud-public-api/platform/v1/connect-query';
import { transport } from '@/api/transport';
import {
  createConnectQueryKey,
  createServiceQueries,
  type MutationFactory,
  queryOptions,
  useMutation,
} from '@/connect-query';
import { reportError } from '@/helpers/errors/report';

export const paymentService = createServiceQueries(PaymentService, {
  getPaymentMethodAvailabilityQuery: (params) =>
    queryOptions(getPaymentMethodAvailability, params, { transport, select: (data) => data.availability?.status }),
  getStripeCheckoutSessionQuery: (params) => queryOptions(getStripeCheckoutSession, params, { transport }),
  listPaymentMethodsQuery: (params) =>
    queryOptions(listPaymentMethods, params, { transport, select: (data) => data.items }),
});

export const useCreateStripeCheckoutSessionMutation: MutationFactory<typeof createStripeCheckoutSession> = () =>
  useMutation(createStripeCheckoutSession, {
    onSuccess: ({ stripeSession }, { accountId }, { router }, { client }) => {
      if (URL.canParse(stripeSession.url ?? '')) {
        return router.navigate({ href: stripeSession.url, reloadDocument: true });
      }

      reportError(
        new Error(
          'Redirecting to Stripe to setup payment method failed - creating a stripe session did not return a `url`.',
        ),
        {
          domain: 'billing',
          action: 'create-stripe-checkout-session',
          extra: { route: router.latestLocation.href },
        },
      );

      return Promise.all([
        client.invalidateQueries({
          queryKey: createConnectQueryKey({
            schema: listPaymentMethods,
            input: { accountId },
          }),
        }),
        client.invalidateQueries({
          queryKey: createConnectQueryKey({ schema: getPaymentMethodAvailability, input: { accountId } }),
        }),
        client.invalidateQueries({
          queryKey: createConnectQueryKey({
            schema: listCloudProviders,
            input: { accountId },
          }),
        }),
        router.invalidate({
          filter: ({ routeId }) => routeId === '/_authenticated/_dashboard/accounts/$accountId',
        }),
      ]);
    },
  });

export const useUpdatePaymentMethodMutation: MutationFactory<typeof updatePaymentMethod> = () =>
  useMutation(updatePaymentMethod, {
    onSuccess: (_data, { paymentMethod: { id, accountId } }, { router }, { client }) =>
      Promise.all([
        client.invalidateQueries({
          queryKey: createConnectQueryKey({ schema: getPaymentMethod, input: { accountId, paymentMethodId: id } }),
        }),
        client.invalidateQueries({
          queryKey: createConnectQueryKey({ schema: listPaymentMethods, input: { accountId } }),
        }),
        client.invalidateQueries({
          queryKey: createConnectQueryKey({ schema: getPaymentMethodAvailability, input: { accountId } }),
        }),
        client.invalidateQueries({
          queryKey: createConnectQueryKey({ schema: listCloudProviders, input: { accountId } }),
        }),
        router.invalidate({
          filter: ({ routeId }) => routeId === '/_authenticated/_dashboard/accounts/$accountId',
        }),
      ]),
  });

export const useDeletePaymentMethodMutation: MutationFactory<typeof deletePaymentMethod> = () =>
  useMutation(deletePaymentMethod, {
    onSuccess: (_data, { accountId, paymentMethodId }, { router }, { client }) => {
      client.removeQueries({
        queryKey: createConnectQueryKey({ schema: getPaymentMethod, input: { accountId, paymentMethodId } }),
      });

      return Promise.all([
        client.invalidateQueries({
          queryKey: createConnectQueryKey({ schema: listPaymentMethods, input: { accountId } }),
        }),
        client.invalidateQueries({
          queryKey: createConnectQueryKey({ schema: getPaymentMethodAvailability, input: { accountId } }),
        }),
        // There are heuristics that determine whether a free_tier cluster is/was available based on the payment marketplace connection.
        client.invalidateQueries({
          queryKey: createConnectQueryKey({ schema: listCloudProviders, input: { accountId } }),
        }),
        router.invalidate({
          filter: ({ routeId }) => routeId === '/_authenticated/_dashboard/accounts/$accountId',
        }),
      ]);
    },
  });

export const useRecordCloudMarketplaceEntitlementMutation: MutationFactory<
  typeof recordCloudMarketplaceEntitlement
> = () =>
  useMutation(recordCloudMarketplaceEntitlement, {
    onSuccess: (_data, { accountId }, { router }, { client }) =>
      Promise.all([
        client.invalidateQueries({
          queryKey: createConnectQueryKey({ schema: listPaymentMethods, input: { accountId } }),
        }),
        client.invalidateQueries({
          queryKey: createConnectQueryKey({ schema: getPaymentMethodAvailability, input: { accountId } }),
        }),
        router.invalidate({ filter: ({ routeId }) => routeId === '/_authenticated/_dashboard/accounts/$accountId' }),
      ]),
  });
